import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Check } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import useDashboardPrefs from '../../hooks/useDashboardPrefs';
import api from '../../services/api';
import useCustomStore from '../../store/customStore';
import { loginUrl } from '../../utils/loginRedirect';
import DeleteAccountModal from '../../components/account/DeleteAccountModal';
import CompteThematiques from './compte/CompteThematiques';
import CompteInfos from './compte/CompteInfos';
import CompteAchats from './compte/CompteAchats';
import CompteFaq from './compte/CompteFaq';
import CompteCgu from './compte/CompteCgu';
import CompteParrainage from './compte/CompteParrainage';

export default function Compte() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, fetchProfile, dashboardPrefs } = useAuthStore();
    const { prefs, updatePrefs, setSelectedScopes, setLastScope, setScopeOrder } = useDashboardPrefs(dashboardPrefs);

    const { sharedThematiques, fetchThematiques: fetchCustomThematiques } = useCustomStore();
    const [scopes, setScopes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteSuccess, setDeleteSuccess] = useState(false);

    const COMPTE_TABS = ['thematiques', 'infos', 'achats', 'parrainage', 'faq', 'cgu'];
    const pathPart = location.pathname.replace(/^\/compte\/account\/?/, '');
    const activeTab = COMPTE_TABS.includes(pathPart) ? pathPart : 'thematiques';

    useEffect(() => {
        if (!user) return;
        fetchProfile();
        fetchCustomThematiques();
        api.get('/scopes/list.php')
            .then(({ data }) => {
                const allScopes = data.data || [];
                setScopes(allScopes);
                setCategories(data.categories || []);
                const selected = prefs.selectedScopes || [];
                const isOnAccountTab = location.pathname.startsWith('/compte/account');
                if (selected.length > 0 && !isOnAccountTab && location.pathname === '/compte') {
                    const target = prefs.lastScope && selected.includes(prefs.lastScope)
                        ? prefs.lastScope : selected[0];
                    if (allScopes.some(s => s.slug === target)) {
                        navigate(`/compte/quiz/${target}`, { replace: true });
                        return;
                    }
                }
            })
            .catch(e => console.warn("Erreur:", e));
    }, []);

    if (!user) return <Navigate to={loginUrl()} replace />;

    const activeScopeIds = (user.subscriptions || []).map(s => s.scope_id);

    return (
        <div className="compte-page">
            {/* L'accueil « Bonjour {prénom}, on travaille sur quoi ? » est désormais affiché
                dans le header sur cette page (à la place du menu thématique, masqué ici) —
                cf. Header.jsx (isThematiqueConfigPage). Plus de bandeau dans le corps de page. */}
            <div className="compte-tab-content">
                {activeTab === 'thematiques' && (
                    <CompteThematiques
                        scopes={scopes} categories={categories} activeScopeIds={activeScopeIds}
                        prefs={prefs} setSelectedScopes={setSelectedScopes} setLastScope={setLastScope}
                        setScopeOrder={setScopeOrder} updatePrefs={updatePrefs}
                        sharedThematiques={sharedThematiques}
                        onRedeemed={() => {
                            api.get('/scopes/list.php').then(({ data }) => { setScopes(data.data || []); setCategories(data.categories || []); });
                            fetchCustomThematiques();
                        }}
                    />
                )}
                {activeTab === 'infos' && <CompteInfos onDeleteAccount={() => setShowDeleteModal(true)} />}
                {activeTab === 'achats' && <CompteAchats />}
                {activeTab === 'parrainage' && <CompteParrainage />}
                {activeTab === 'faq' && <CompteFaq />}
                {activeTab === 'cgu' && <CompteCgu />}
            </div>

            {showDeleteModal && !deleteSuccess && (
                <DeleteAccountModal
                    onClose={() => setShowDeleteModal(false)}
                    onSuccess={() => {
                        setDeleteSuccess(true);
                        setTimeout(() => {
                            logout();
                            navigate('/', { replace: true });
                        }, 2500);
                    }}
                />
            )}

            {deleteSuccess && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                    zIndex: 9300, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 16,
                }}>
                    <div style={{
                        background: 'var(--card-bg, #1e293b)',
                        color: 'var(--text, #e2e8f0)',
                        border: '1px solid var(--border, #334155)',
                        borderRadius: 14,
                        padding: 32,
                        maxWidth: 420,
                        textAlign: 'center',
                        boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                    }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'rgba(16,185,129,0.15)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            marginBottom: 12,
                        }}>
                            <Check size={28} style={{ color: '#10b981' }} strokeWidth={3} />
                        </div>
                        <h3 style={{ margin: '0 0 8px' }}>Compte supprimé</h3>
                        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.92em' }}>
                            Ton compte et tes données ont été effacés. Un email de confirmation a été envoyé à ton adresse.
                        </p>
                        <p style={{ margin: '12px 0 0', color: 'var(--text-muted)', fontSize: '0.82em', fontStyle: 'italic' }}>
                            Redirection vers l'accueil…
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
